import { invalidateRecordCachesExceptSubject } from '@Data/hooks/recordCacheInvalidation';
import {
  DELETE_CONFIRMATION_HEADER,
  DELETE_CONFIRMATION_HEADER_VALUE,
  type DeleteResourceRecordErrorBody
} from '@Data/hooks/useDeleteResourceRecord';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import { assertResourceMethodAvailable } from '@Pages/pageRuntime/sdk/endpointGating';
import type { SdkApiError, SdkResource, ResourceUrls } from '@Pages/pageRuntime/sdk/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';

import type { OperationMetadata } from './recordUpdateInteractions';

type PreviewData = Diagonal.Data.Data.RecordDeletionPreviewData;

export interface DeleteResourceRecordState {
  preview: PreviewData | undefined;
  isPreviewLoading: boolean;
  isPreviewError: boolean;
  confirm: (operation?: OperationMetadata) => void;
  isProcessing: boolean;
  /**
   * Structured error body returned by the API on a blocked delete response.
   * `null` for successful deletes and generic transport errors.
   */
  deleteError: DeleteResourceRecordErrorBody | null;
  /** Normalized failed delete request, or `null`. */
  error: SdkApiError | null;
  reset: () => void;
}

export type DeleteResourceRecordResource = Pick<
  SdkResource,
  'id' | 'name' | 'disabled_data_api_endpoints'
> & {
  urls: Pick<ResourceUrls, 'delete'>;
};

interface UseDeleteResourceRecordOptions {
  resource: DeleteResourceRecordResource;
  recordId: string | undefined;
  /**
   * Whether to fetch the delete preview now. Open this gate when the
   * confirmation dialog is opened so the preview matches the moment of decision.
   */
  fetchPreview?: boolean;
  onSuccess?: () => void;
}

export function useDeleteResourceRecord({
  resource,
  recordId,
  fetchPreview = false,
  onSuccess
}: UseDeleteResourceRecordOptions): DeleteResourceRecordState {
  const queryClient = useQueryClient();

  const previewUrl = recordId
    ? `/api/data/resources/${encodeURIComponent(resource.id)}/records/${encodeURIComponent(recordId)}/delete-preview`
    : '';

  const previewQuery = useQuery<PreviewData>({
    enabled: fetchPreview && Boolean(recordId),
    queryKey: ['record-delete-preview', resource.id, recordId, previewUrl],
    queryFn: () => axios.get(previewUrl).then((response) => response.data)
  });

  const deleteMutation = useMutation<
    unknown,
    AxiosError<DeleteResourceRecordErrorBody>,
    OperationMetadata | undefined
  >({
    mutationFn: (operation?: OperationMetadata) => {
      assertResourceMethodAvailable(resource, 'delete');

      return axios.delete(resource.urls.delete(recordId as string), {
        headers: {
          [DELETE_CONFIRMATION_HEADER]: DELETE_CONFIRMATION_HEADER_VALUE
        },
        data: operation ? { $operation: operation } : undefined
      });
    },
    onSuccess: () => {
      void invalidateRecordCachesExceptSubject(queryClient, resource.id, recordId);
      onSuccess?.();
    }
  });

  const deleteError = (() => {
    const error = deleteMutation.error;
    if (!error || !axios.isAxiosError<DeleteResourceRecordErrorBody>(error)) {
      return null;
    }
    const body = error.response?.data;
    if (
      error.response?.status !== 409 ||
      !body ||
      typeof body !== 'object' ||
      body.outcome === null ||
      typeof body.outcome !== 'object'
    ) {
      return null;
    }
    return body;
  })();
  const error = deleteMutation.error ? toApiError(deleteMutation.error) : null;

  return {
    preview: previewQuery.data,
    isPreviewLoading: previewQuery.isLoading,
    isPreviewError: previewQuery.isError,
    confirm: (operation?: OperationMetadata) => {
      if (!recordId) {
        return;
      }
      deleteMutation.mutate(operation);
    },
    isProcessing: deleteMutation.isPending,
    deleteError,
    error,
    reset: () => {
      deleteMutation.reset();
    }
  };
}
